/**
 * Brain-dump notes (R8.2/R8.1, Phase 6, ADR-0002 addendum 2026-07-07,
 * "D-Notes"): capture-only, never checkable, never dated, never
 * objective-linked while it remains a note (Non-Goal #1's narrow carve-out,
 * PRD §3/§2.1). Exactly two ways to leave the list: delete, or promote
 * (which consumes it and creates the target elsewhere).
 *
 * Same module shape as every other commands+projection pair in this
 * codebase: pure functions over the event log, no I/O (ADR-0001 rule 2).
 */

import type {
  ActionId,
  ClockPort,
  CompassEvent,
  IdPort,
  NoteCreatedEvent,
  NoteDeletedEvent,
  NoteEditedEvent,
  NoteId,
  NotePromotedEvent,
  NotePromotionTarget,
  NoteView,
  ObjectiveId,
  PriorityId,
} from '../../contracts';
import { type CommandResult, domainError } from '../event-log/command-error';
import { eventsOfTypes } from '../event-log/event-log';

type Ports = { readonly clock: ClockPort; readonly id: IdPort };

interface NoteState {
  readonly noteId: NoteId;
  text: string;
  /** Set once deleted or promoted -- either way the note leaves the list (spec §2.3: "identically for list-membership purposes"). */
  gone: boolean;
}

/** Rebuild every note's current state from the log, in canonical (eventId) order. */
function buildNoteStates(events: readonly CompassEvent[]): Map<NoteId, NoteState> {
  const states = new Map<NoteId, NoteState>();
  for (const event of eventsOfTypes(events, [
    'note.created',
    'note.edited',
    'note.deleted',
    'note.promoted',
  ])) {
    switch (event.type) {
      case 'note.created':
        states.set(event.noteId, { noteId: event.noteId, text: event.text, gone: false });
        break;
      case 'note.edited': {
        const state = states.get(event.noteId);
        if (state) state.text = event.text;
        break;
      }
      case 'note.deleted': {
        const state = states.get(event.noteId);
        if (state) state.gone = true;
        break;
      }
      case 'note.promoted': {
        const state = states.get(event.noteId);
        if (state) state.gone = true;
        break;
      }
      default:
        break;
    }
  }
  return states;
}

/**
 * Unsorted brain-dump list (AC-T1): every note that has never been deleted
 * or promoted. The domain layer imposes no ordering guarantee beyond stable
 * Map iteration (insertion order); a caller wanting a specific display order
 * sorts itself (contract doc comment).
 */
export function listNotes(events: readonly CompassEvent[]): readonly NoteView[] {
  const states = buildNoteStates(events);
  const views: NoteView[] = [];
  for (const state of states.values()) {
    if (state.gone) continue;
    views.push({ noteId: state.noteId, text: state.text });
  }
  return views;
}

export function createNote(
  text: string,
  ports: Ports,
): CommandResult<NoteCreatedEvent> {
  if (text.trim().length === 0) {
    throw domainError('invalid_input', 'note text must not be empty');
  }

  const event: NoteCreatedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'note.created',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    noteId: ports.id.nextRawId() as NoteId,
    text,
  };

  return { events: [event] };
}

function requireLiveNote(events: readonly CompassEvent[], noteId: NoteId): NoteState {
  const state = buildNoteStates(events).get(noteId);
  if (!state || state.gone) {
    throw domainError('note_not_found', `note ${noteId} not found`);
  }
  return state;
}

/** In-place text correction only (spec §2.1) -- never a completion state. */
export function editNote(
  events: readonly CompassEvent[],
  noteId: NoteId,
  text: string,
  ports: Ports,
): CommandResult<NoteEditedEvent> {
  requireLiveNote(events, noteId);
  if (text.trim().length === 0) {
    throw domainError('invalid_input', 'note text must not be empty');
  }

  const event: NoteEditedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'note.edited',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    noteId,
    text,
  };

  return { events: [event] };
}

export function deleteNote(
  events: readonly CompassEvent[],
  noteId: NoteId,
  ports: Ports,
): CommandResult<NoteDeletedEvent> {
  requireLiveNote(events, noteId);

  const event: NoteDeletedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'note.deleted',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    noteId,
  };

  return { events: [event] };
}

/**
 * Promotion target requested by the caller (`DomainApi.promoteNote`'s own
 * narrower union -- objectiveId only for the action target, since the
 * actionId/priorityId are minted here, not supplied by the caller).
 */
export type NotePromotionRequest =
  | { readonly kind: 'action'; readonly objectiveId: ObjectiveId }
  | { readonly kind: 'priority' };

/**
 * The only bridge from a note into actionable work (spec §2.3, AC-T2).
 * Sequencing per the ADR addendum: the target-creating event (`action.added`
 * or `priority.created`) is appended FIRST, then `note.promoted` referencing
 * the resulting ID -- callers must append BOTH events in the returned order
 * (this function returns them pre-ordered) so a reader never observes a
 * `note.promoted` event whose target doesn't yet exist in log order.
 *
 * This function only constructs the note-side event pairing; the actual
 * target-creating event is caller-supplied (`buildTargetEvent`) since minting
 * an `action.added`/`priority.created` event requires validation this module
 * does not own (objective existence, the 5-Priority cap) -- the DomainApi
 * layer composes those checks (via `objectives/commands.ts`'s `addAction` /
 * `today/priorities.ts`'s `createPriority`) and passes the resulting event in.
 */
export function promoteNote(
  events: readonly CompassEvent[],
  noteId: NoteId,
  targetEvent:
    | { readonly kind: 'action'; readonly objectiveId: ObjectiveId; readonly actionId: ActionId }
    | { readonly kind: 'priority'; readonly priorityId: PriorityId },
  ports: Ports,
): CommandResult<NotePromotedEvent> {
  requireLiveNote(events, noteId);

  const target: NotePromotionTarget =
    targetEvent.kind === 'action'
      ? { kind: 'action', objectiveId: targetEvent.objectiveId, actionId: targetEvent.actionId }
      : { kind: 'priority', priorityId: targetEvent.priorityId };

  const event: NotePromotedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'note.promoted',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    noteId,
    target,
  };

  return { events: [event] };
}
